/* Ambient duck "bots" — 31-43 lifelike fake players that roam the whole city, hang out in rooms,
   emote, dance, eat and travel so the world always feels packed. They never open stores or sell
   anything. Rendered through the exact same `others` pipeline as real players and blended into the
   online count. */
import { WORLD_W, BUILDINGS, roomLayout } from "./cityData";

const PRE = ["Sir", "Lil", "DJ", "MC", "Big", "Prof", "Capt", "Baron", "Duke", "Miss", "Lady", "King", "Ace", "Neo", "Vibe", "Uncle", "Auntie", "Young", "Sunny", "Baby"];
const CORE = ["Quackers", "Mallard", "Feathers", "Webby", "Puddles", "Drake", "Waddles", "Splash", "Nugget", "Biscuit", "Maple", "Pixel", "Turbo", "Coco", "Mango", "Ziggy", "Beaks", "Honk", "Ripple", "Skye", "Comet", "Django", "Gizmo", "Pepper", "Waffles", "Marina", "Dizzy", "Rello", "Cash", "Beans", "Tofu", "Olive"];
const SHIRT = ["#ef4444", "#f59e0b", "#22c55e", "#06b6d4", "#3b82f6", "#8b5cf6", "#ec4899", "#111827", "#f8fafc", "#14b8a6", "#fb7185"];
const GLASS = [null, null, null, "aviator", "visor", "shutter"];
const CHAIN = [null, null, null, null, "gold", "diamond", "dollar"];
const CAPE = [null, null, null, null, null, "red", "blue", "gold"];
const HAIR = [null, null, null, "blonde", "pink", "blue", "purple"];
const SAY = ["brb, snack run", "this fit is fire", "who wants to race?", "grinding coins today", "love this song", "new drip who dis", "meet me at the lounge", "util under 10% lets go", "my score is climbing!", "first storefront soon", "catch me at the arcade", "waddle on", "big moves today", "vibes only", "need a coffee", "lets goooo", "spinning the jukebox", "found a rare drop!", "see you at the square", "new city unlocked", "this beat slaps", "who's dancing?", "payday feeling"];
const SAY_EVT = ["COIN RAIN!!", "get the villain!", "flash sale, go go", "parade time!", "whoa, a meteor", "lights out?!"];
const GREET = ["👋 hey!", "hi there!", "yo, welcome!", "waddle up!", "👋 what's good?", "hey friend!", "nice to see ya!"];
const WAVE_BACK = ["👋 waves back!", "hey you!", "yo yo!", "ayy hi!", "👋 hi hi!", "heyyy!", "waddle squad!"];
const TEXT_REPLY = ["lol same", "haha for real", "see you at the lounge!", "nice, let's link", "adding you rn", "omw", "🔥🔥", "waddle squad", "catch you in the city", "bet!", "lets race sometime", "love that"];
const pick = (a) => a[Math.floor(Math.random() * a.length)];
const perfNow = () => (typeof performance !== "undefined" ? performance.now() : Date.now());
const REGULARS = [
  { name: "Mayor Mallard", home: "square", hat: 3, outfit: { shirt: { base: "#1e3a8a", kind: "tee" }, chain: "gold" } },
  { name: "DJ Ziggy", home: "nqc", hat: 5, outfit: { shirt: { base: "#111827", kind: "hoodie" }, glasses: "shutter", hair: "pink" } },
  { name: "Granny Puddles", home: "square", outfit: { shirt: { base: "#f472b6", kind: "tee" }, hair: "blonde" } },
  { name: "Baron Cash", home: "casino", hat: 1, outfit: { shirt: { base: "#065f46", kind: "tee" }, chain: "dollar", glasses: "aviator" } },
  { name: "Coach Beaks", home: "park", outfit: { shirt: { base: "#ea580c", kind: "tee" }, cape: "red" } },
  { name: "Miss Marina", home: "beach", outfit: { shirt: { base: "#06b6d4", kind: "tee" }, glasses: "visor" } },
];

function botName() {
  if (Math.random() < 0.2) return `Duck-${1000 + Math.floor(Math.random() * 9000)}`;
  return `${pick(PRE)} ${pick(CORE)}`.slice(0, 16);
}
function botOutfit() {
  const o = {};
  if (Math.random() < 0.6) o.shirt = { base: pick(SHIRT), kind: Math.random() < 0.28 ? "hoodie" : "tee" };
  const g = pick(GLASS); if (g) o.glasses = g;
  const c = pick(CHAIN); if (c) o.chain = c;
  const cp = pick(CAPE); if (cp) o.cape = cp;
  const h = pick(HAIR); if (h) o.hair = h;
  return o;
}

export function createBots() {
  const ids = BUILDINGS.map((b) => b.id).filter((id) => id && id !== "ridestand");
  const SOCIAL = new Set(["square", "nqc", "casino", "arcade", "mall", "beach", "park"].filter((id) => ids.includes(id)));
  const pool = [];
  for (const id of ids) pool.push(id);
  for (let i = 0; i < ids.length; i++) pool.push("street"); // ~50% street so Main Street stays busy

  const widthCache = {};
  const width = (scene) => {
    if (scene === "street") return WORLD_W;
    if (widthCache[scene]) return widthCache[scene];
    let w = 1600; try { w = roomLayout(scene).w || 1600; } catch { w = 1600; }
    widthCache[scene] = w; return w;
  };
  const randX = (scene) => 150 + Math.random() * Math.max(320, width(scene) - 300);

  const N = 43;
  const bots = Array.from({ length: N }, (_, i) => {
    const scene = pick(pool);
    return { pid: `bot_${i}_${Math.random().toString(36).slice(2, 7)}`, name: botName(), hat: Math.floor(Math.random() * 6), outfit: botOutfit(), scene, x: randX(scene), tx: 0, facing: Math.random() < 0.5 ? 1 : -1, moving: true, online: i < 38, say: null, sayUntil: 0, dance: false, held: null, nextT: 0, speed: 52 + Math.random() * 46, seed: Math.random() * 10 };
  });
  bots.forEach((b) => (b.tx = randX(b.scene)));
  REGULARS.forEach((rg, i) => { if (i >= bots.length) return; const b = bots[i]; b.name = rg.name; if (rg.hat != null) b.hat = rg.hat; b.outfit = rg.outfit || b.outfit; b.regular = true; b.home = ids.includes(rg.home) ? rg.home : "street"; b.scene = b.home; b.x = randX(b.scene); b.tx = randX(b.scene); b.online = true; });

  let evtActive = false, evtFocus = null;

  let target = 31 + Math.floor(Math.random() * 13); // 31..43
  let driftAcc = 0;

  const decide = (b, now, evt, scene) => {
    b.dance = false; b.held = null;
    if (evtActive) {
      if (b.scene === "street") { b.tx = (evtFocus != null ? evtFocus : b.x) + (Math.random() * 480 - 240); b.moving = true; if (Math.random() < 0.5) { b.say = pick(SAY_EVT); b.sayUntil = now + 3000; } b.nextT = now + 900 + Math.random() * 1500; return; }
      if (Math.random() < 0.4) { b.scene = "street"; b.x = randX("street"); b.tx = (evtFocus != null ? evtFocus : b.x) + (Math.random() * 480 - 240); b.moving = true; b.nextT = now + 1100 + Math.random() * 1200; return; }
    }
    const r = Math.random();
    if (r < 0.16) { b.scene = b.regular && Math.random() < 0.6 ? b.home : pick(pool); b.x = randX(b.scene); b.tx = randX(b.scene); b.moving = true; }
    else if (r < 0.72) { b.tx = randX(b.scene); b.moving = true; }
    else {
      b.moving = false; b.tx = b.x;
      const nearYou = scene && b.scene === scene;
      const h = Math.random();
      if (nearYou && h < 0.4) { b.say = pick(GREET); b.sayUntil = now + 3600; }
      else if (h < 0.42) { b.say = evt && Math.random() < 0.5 ? pick(SAY_EVT) : pick(SAY); b.sayUntil = now + 3600; }
      else if (h < 0.74 && (SOCIAL.has(b.scene) || Math.random() < 0.25)) b.dance = true;
      else if (h < 0.9) b.held = Math.random() < 0.5 ? "cup" : "sandwich";
      else { b.say = pick(SAY); b.sayUntil = now + 3200; }
    }
    b.nextT = now + 2200 + Math.random() * 5200;
  };

  return {
    step(dt, { event = false, now = perfNow(), scene = null, focusX = null } = {}) {
      evtActive = !!event; evtFocus = focusX;
      driftAcc += dt;
      if (driftAcc > 7) {
        driftAcc = 0;
        if (Math.random() < 0.6) target = 31 + Math.floor(Math.random() * 13);
        const onCount = bots.filter((b) => b.online).length;
        if (onCount < target) { const off = bots.find((b) => !b.online); if (off) { off.online = true; off.scene = pick(pool); off.x = randX(off.scene); off.tx = randX(off.scene); off.moving = true; off.nextT = 0; } }
        else if (onCount > target) { const on = bots.filter((b) => b.online); if (on.length) on[Math.floor(Math.random() * on.length)].online = false; }
      }
      for (const b of bots) {
        if (!b.online) continue;
        if (now >= b.nextT) decide(b, now, event, scene);
        if (b.moving) {
          const dx = b.tx - b.x; const stepPx = b.speed * dt;
          if (Math.abs(dx) <= stepPx * 1.15) { b.x = b.tx; b.moving = false; }
          else { b.x += Math.sign(dx) * stepPx; b.facing = dx > 0 ? 1 : -1; }
        }
        if (b.say && now > b.sayUntil) b.say = null;
      }
    },
    snapshot() {
      return bots.filter((b) => b.online).map((b) => {
        const outfit = (b.dance || b.held) ? { ...b.outfit, ...(b.dance ? { dance: true } : {}), ...(b.held ? { held: b.held } : {}) } : b.outfit;
        return { pid: b.pid, name: b.name, scene: b.scene, x: Math.round(b.x), facing: b.facing, moving: b.moving, alt: 0, hat: b.hat, car: null, outfit, say: b.say, bot: true, seed: b.seed };
      });
    },
    onlineCount() { return bots.filter((b) => b.online).length; },
    get(pid) { return bots.find((b) => b.pid === pid) || null; },
    react(pid, kind, now = perfNow()) {
      const b = bots.find((x) => x.pid === pid); if (!b) return null;
      b.online = true; b.moving = false; b.dance = false; b.held = null;
      const line = kind === "text" ? pick(TEXT_REPLY) : pick(WAVE_BACK);
      b.say = line; b.sayUntil = now + 4200; b.nextT = now + 2600;
      return line;
    },
  };
}
